/**
 * useCollateralPolicy Hook
 *
 * Server-authoritative collateral policy management. Replaces the
 * usePlatformConfig('collateral-policy') pattern that stored policy
 * as a generic JSON blob.
 *
 * When a tenant has no saved policy, loads the baseline its institution type
 * selects as starting config; a type that selects none leaves the page with
 * nothing to edit (baselineMissing).
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import {
  collateralPolicyApi,
  type CollateralPolicy,
  type CollateralPolicyUpdate,
  type CollateralPolicySave,
  type PolicyValidationResult,
  type PolicyAuditEntry,
  type BaselinePolicy,
} from '../api/services/collateral-policy';

export type { CollateralPolicy, CollateralPolicyUpdate, CollateralPolicySave, PolicyValidationResult, PolicyAuditEntry, BaselinePolicy };

/**
 * The baseVersion a full save composed from `policy` names: its saved version,
 * or null when it was built from the baseline and no version is saved
 * (R1c4c-f1).
 */
export function composedFromVersion(policy: CollateralPolicy | null | undefined): number | null {
  const version = policy?.metadata?.version;
  return typeof version === 'number' && version >= 1 ? version : null;
}

/**
 * True when the policy is the Aaim baseline rather than one this institution
 * saved.
 *
 * GET /collateral-policy/:tenantId answers 404 when the tenant has saved
 * nothing, and the hook builds the page from GET /baselines with
 * `metadata.version: 0`. A saved policy's version starts at 1
 * (PolicyMetadata.version, minimum 1). Until R1c2f the route itself served the
 * baseline as version 0; the signal before that, `!policyQuery.data &&
 * !!baselineQuery.data`, was false whenever that 200 arrived, so the baseline
 * notice could never render (UI run 2026-09-27, fi-g7-1).
 */
export function isBaselinePolicy(policy: Pick<CollateralPolicy, 'metadata'> | null | undefined): boolean {
  const version = policy?.metadata?.version;
  return typeof version === 'number' && version < 1;
}

/** How often the policy is re-read while its consumer push has no recorded answer. */
export const CONSUMER_SYNC_POLL_MS = 3_000;

/** How long after a version takes effect the page keeps re-reading for the push answer. */
export const CONSUMER_SYNC_POLL_WINDOW_MS = 120_000;

/**
 * The refetch interval for the policy query. A save answers
 * metadata.consumerSync PENDING: platform-admin-api pushes the version to the
 * member platform after the PUT returns and records the answer on the version
 * row (GH #2841). The page re-reads while the answer is pending, for two minutes
 * after the version took effect; a version still pending after that stays
 * reported as pending until the next read.
 */
export function consumerSyncRefetchInterval(
  policy: Pick<CollateralPolicy, 'metadata'> | null | undefined,
  now: number,
): number | false {
  if (policy?.metadata?.consumerSync?.status !== 'PENDING') return false;
  const effectiveAt = Date.parse(policy.metadata.effectiveDate);
  return Number.isFinite(effectiveAt) && now - effectiveAt < CONSUMER_SYNC_POLL_WINDOW_MS
    ? CONSUMER_SYNC_POLL_MS
    : false;
}

export function useCollateralPolicy() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const tenantId = user?.tenantId ?? '';

  // Fetch active policy
  const policyQuery = useQuery({
    queryKey: ['collateral-policy', tenantId],
    queryFn: () => collateralPolicyApi.getPolicy(tenantId),
    enabled: !!tenantId,
    retry: 1,
    refetchInterval: (query) => consumerSyncRefetchInterval(query.state.data, Date.now()),
  });

  // Fetch the baseline this tenant's institution type selects (used when the
  // tenant has no policy), for the same tenant the policy is read for (R1c3).
  // The cache is keyed by tenant: a session that moves to another tenant must
  // not read the previous tenant's row.
  const baselineQuery = useQuery({
    queryKey: ['collateral-policy-baseline', tenantId],
    queryFn: () => collateralPolicyApi.getBaseline(tenantId),
    enabled: !!tenantId,
    retry: 1,
  });

  // Fetch audit trail
  const auditQuery = useQuery({
    queryKey: ['collateral-policy-audit', tenantId],
    queryFn: () => collateralPolicyApi.getAuditTrail(tenantId),
    enabled: !!tenantId,
    retry: 1,
  });

  // Save mutation. Guarded against empty tenantId -- without the guard the
  // openapi-fetch path-substitution produces "/collateral-policy//validate"
  // (double slash) and the request 404s. The page surface for collateral
  // policy is only meaningful inside a tenant scope, so a missing tenantId
  // is a programming error worth surfacing.
  const saveMutation = useMutation({
    mutationFn: (update: CollateralPolicyUpdate | CollateralPolicySave) => {
      if (!tenantId) {
        throw new Error('Cannot save collateral policy without an active tenant context');
      }
      // A full save names the version the page was composed from, null when
      // the page was built from the baseline. platform-admin-api compares it
      // with the active version under the tenant's lock and answers 409 when
      // another save landed first (R1c4c-f1).
      //
      // A caller that edits a copy of the policy names the version of that
      // copy (composedFromVersion). The policy held here can move to a newer
      // version while a save is on its way, on a poll or a refetch, and a body
      // composed from version N must never be sent as composed from N+1. A
      // caller that names no version saves what this hook holds.
      const baseVersion = 'baseVersion' in update
        ? update.baseVersion
        : composedFromVersion(effectivePolicy);
      return collateralPolicyApi.updatePolicy(tenantId, { ...update, baseVersion });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(['collateral-policy', tenantId], result);
      queryClient.invalidateQueries({ queryKey: ['collateral-policy-audit', tenantId] });
    },
  });

  // Validate mutation (dry-run). Same guard as saveMutation -- without it
  // the URL becomes "/collateral-policy//validate" when tenantId is empty.
  const validateMutation = useMutation({
    mutationFn: (proposed: CollateralPolicyUpdate) => {
      if (!tenantId) {
        throw new Error('Cannot validate collateral policy without an active tenant context');
      }
      return collateralPolicyApi.validatePolicy(tenantId, proposed);
    },
  });

  // Resolve effective policy: the saved policy, or the baseline as the
  // starting point. GET /collateral-policy/{tenantId} answers 404 for a tenant
  // with no saved policy (getPolicy maps it to null). GET /baselines answers a
  // BaselinePolicy; its monitoringDefaults becomes the policy's
  // monitoringPolicy. Version 0 marks a page built from the baseline
  // (isBaselinePolicy).
  //
  // Memoized so the reference is stable across renders. Without useMemo, the
  // baseline-fallback branch constructs a new object every render and the
  // parent CollateralPolicyPage's useEffect on `serverPolicy` re-fires (its
  // JSON.stringify comparison detects the new effectiveDate timestamp), which
  // calls setPolicy(serverPolicy) and wipes user edits on every keystroke.
  // Use baseline.generatedAt when available to keep the timestamp stable
  // across renders; otherwise compute once via useMemo.
  const baselineData = baselineQuery.data ?? null;
  const effectivePolicy = useMemo<CollateralPolicy | null>(() => {
    if (policyQuery.data) return policyQuery.data;
    if (!baselineData) return null;
    return {
      tenantId,
      assetClassPolicies: baselineData.assetClassPolicies,
      concentrationPolicy: baselineData.concentrationPolicy,
      monitoringPolicy: baselineData.monitoringDefaults,
      metadata: {
        effectiveDate: baselineData.generatedAt ?? new Date().toISOString(),
        version: 0,
        regulatoryBasis: ['Baseline configuration'],
      },
    };
  }, [policyQuery.data, baselineData, tenantId]);

  const isNewPolicy = isBaselinePolicy(effectivePolicy);

  // No saved policy, and no baseline row for this institution's type: GET
  // /baselines answers 404 BASELINE_NOT_SELECTED when the type selects no row
  // (R1c3, ruled 2026-10-02), which getBaseline maps to null. The page has
  // nothing to edit.
  const baselineMissing =
    policyQuery.isSuccess && !policyQuery.data && baselineQuery.isSuccess && baselineQuery.data === null;

  return {
    policy: effectivePolicy,
    isLoading: policyQuery.isLoading || baselineQuery.isLoading,
    // A failed baseline read matters when no policy is saved: the page is then
    // built from the baseline. Until R1c3 it rendered empty editors.
    error: policyQuery.error ?? (policyQuery.data ? null : baselineQuery.error),
    isNewPolicy,
    baselineMissing,
    // A session with no tenant (an Aaim operator who impersonates none) runs
    // no query, so neither the loading state nor a result arrives.
    noTenantSelected: !tenantId,
    baseline: baselineQuery.data,

    save: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    saveError: saveMutation.error,

    validate: validateMutation.mutate,
    isValidating: validateMutation.isPending,
    validationResult: validateMutation.data ?? null,

    auditEntries: auditQuery.data?.entries ?? [],
    auditTotal: auditQuery.data?.total ?? 0,
    isLoadingAudit: auditQuery.isLoading,
    refetchAudit: auditQuery.refetch,
  };
}
